import { LRUCache } from 'lru-cache';
import { extractAssetId } from './asset-utils';

type MediaType = 'video' | 'audio' | 'image';

interface CacheMetadata {
  size: number;
  type: MediaType;
}

class MediaCache {
  // Cache configuration constants (configurable via environment variables)
  private static readonly CACHE_TTL_MS = Number(import.meta.env.VITE_CACHE_TTL_MS) || (1000 * 60 * 60 * 24 * 7); // 7 days
  private static readonly MAX_CACHE_ENTRIES = Number(import.meta.env.VITE_MAX_CACHE_ENTRIES) || 1000;
  private static readonly DEFAULT_MAX_CACHE_SIZE = Number(import.meta.env.VITE_MAX_CACHE_SIZE) || (10 * 1024 * 1024); // 10MB

  private cacheName = 'media-cache-v1';
  private maxCacheSize = MediaCache.DEFAULT_MAX_CACHE_SIZE;
  private cacheVersionKey = '__cache_version__'; // Special key for storing cache version
  private currentCacheVersion: string;
  private initializationPromise: Promise<void> | null = null;
  
  private blobUrlToAssetId = new Map<string, string>(); // Track blob URLs for cleanup
  private downloadingAssets = new Map<string, Promise<Blob>>(); // Prevent duplicate downloads
  private abortControllers = new Map<string, AbortController>(); // Track AbortControllers for active downloads
  
  // Use LRU cache to manage metadata and access order
  // Note: dispose callback uses arrow function to preserve 'this' context
  private metadataCache = new LRUCache<string, CacheMetadata>({
    max: MediaCache.MAX_CACHE_ENTRIES,
    maxSize: this.maxCacheSize, // Maximum total size in bytes
    ttl: MediaCache.CACHE_TTL_MS,
    sizeCalculation: (value) => value.size, // Calculate size from metadata
    updateAgeOnGet: true, // Update access time on get (LRU behavior)
    updateAgeOnHas: false, // Don't update on has check
    dispose: async (value, key) => {
      // When LRU cache evicts an entry, also remove it from Cache API
      await this.handleEviction(key, value);
    },
  });

  constructor() {
    // Get cache version from environment variable, default to "1"
    this.currentCacheVersion = import.meta.env.VITE_MEDIA_CACHE_VERSION || '1';
  }

  // Ensure cache is initialized (version check) before use
  private async ensureInitialized(): Promise<void> {
    // Check if Cache API is available
    if (!('caches' in window)) {
      console.warn('Cache API not available, caching will be disabled');
      return;
    }
    
    if (!this.initializationPromise) {
      this.initializationPromise = this.checkCacheVersion();
    }
    return this.initializationPromise;
  }

  // Check cache version and clear if version mismatch
  private async checkCacheVersion(): Promise<void> {
    try {
      const cache = await caches.open(this.cacheName);
      const versionRequest = this.createCacheRequest(this.cacheVersionKey);
      const versionResponse = await cache.match(versionRequest);

      if (versionResponse) {
        const storedVersion = await versionResponse.text();
        
        // If version doesn't match, clear all cache
        if (storedVersion !== this.currentCacheVersion) {
          console.log(
            `Cache version mismatch: stored=${storedVersion}, current=${this.currentCacheVersion}. Clearing cache...`
          );
          // Clear cache without calling checkCacheVersion again to avoid recursion
          await this.clearCacheWithoutVersionCheck();
        }
      }

      // Store current version (if not already stored or after clearing)
      const newVersionResponse = new Response(this.currentCacheVersion, {
        headers: {
          'Content-Type': 'text/plain',
        },
      });
      await cache.put(versionRequest, newVersionResponse);
    } catch (error) {
      console.error('Error checking cache version:', error);
    }
  }

  // Clear cache without version check (internal use to avoid recursion)
  private async clearCacheWithoutVersionCheck(): Promise<void> {
    try {
      // Revoke all blob URLs
      for (const blobUrl of this.blobUrlToAssetId.keys()) {
        URL.revokeObjectURL(blobUrl);
      }
      this.blobUrlToAssetId.clear();

      // Clear Cache API storage
      await caches.delete(this.cacheName);

      // Clear LRU cache metadata
      this.metadataCache.clear();
      this.downloadingAssets.clear();
      this.abortControllers.clear();

      console.log('Media cache cleared (version mismatch)');
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }
  
  // Handle eviction from both LRU cache and Cache API
  private async handleEviction(cacheKey: string, metadata: CacheMetadata): Promise<void> {
    try {
      const cache = await caches.open(this.cacheName);
      const request = this.createCacheRequest(cacheKey);
      await cache.delete(request);
      
      // Deliberately does NOT revoke this key's blob URLs. Eviction frees
      // cache storage; it has no idea whether a <video>/<audio> element is
      // playing from one of those URLs right now. Revoking one kills playback
      // with ERR_FILE_NOT_FOUND, and the element cannot recover: play() then
      // rejects with NotSupportedError and the stage never advances.
      // Each holder revokes the URL it owns - see useMediaCache's cleanup.

      console.log(`Auto-evicted from cache: ${cacheKey} (freed ${(metadata.size / 1024 / 1024).toFixed(2)} MB)`);
    } catch (error) {
      console.error('Error during auto-eviction:', error);
    }
  }

  // Extract asset ID from URL (filename without signature)
  // Uses shared utility function
  private extractAssetId(url: string): string | undefined {
    return extractAssetId(url);
  }

  // Determine media type from URL or content type
  private getMediaType(url: string, contentType?: string): MediaType {
    if (contentType) {
      if (contentType.startsWith('video/')) return 'video';
      if (contentType.startsWith('audio/')) return 'audio';
      if (contentType.startsWith('image/')) return 'image';
    }

    // Fallback to URL extension
    const extension = url.split('.').pop()?.toLowerCase();
    if (['mp4', 'webm', 'mov', 'avi'].includes(extension || '')) return 'video';
    if (['mp3', 'wav', 'ogg', 'm4a'].includes(extension || '')) return 'audio';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension || '')) return 'image';

    return 'image'; // Default fallback
  }

  // Get cache key for asset
  private getCacheKey(assetId: string, type: MediaType): string {
    return `${type}:${assetId}`;
  }

  // Create a valid Request object for Cache API
  // Cache API requires a valid URL scheme (http/https), so we use a virtual URL
  private createCacheRequest(cacheKey: string): Request {
    return new Request(`https://cache.local/${cacheKey}`, {
      method: 'GET',
    });
  }

  // Get cached blob URL
  async getCachedUrl(url: string, type?: MediaType): Promise<string | null> {
    // Check if Cache API is available
    if (!('caches' in window)) {
      return null;
    }
    
    const assetId = this.extractAssetId(url);
    if (!assetId) return null;

    const mediaType = type || this.getMediaType(url);
    const cacheKey = this.getCacheKey(assetId, mediaType);

    try {
      const cache = await caches.open(this.cacheName);
      const request = this.createCacheRequest(cacheKey);
      const cachedResponse = await cache.match(request);

      if (cachedResponse) {
        // Check if metadata exists and is not expired
        const metadata = this.metadataCache.get(cacheKey);
        if (!metadata) {
          // Metadata expired or missing, treat as cache miss
          await cache.delete(request);
          return null;
        }

        const blob = await cachedResponse.blob();
        const blobUrl = this.createBlobUrl(blob, cacheKey);
        
        console.log(`Cache hit: ${cacheKey} (${(blob.size / 1024 / 1024).toFixed(2)} MB)`);
        
        return blobUrl;
      }
    } catch (error) {
      console.error('Error reading from cache:', error);
    }

    return null;
  }

  // Download and cache media asset
  async downloadAndCache(
    url: string,
    type?: MediaType,
    signal?: AbortSignal,
  ): Promise<string> {
    // Ensure cache is initialized before use
    await this.ensureInitialized();

    const assetId = this.extractAssetId(url);
    if (!assetId) {
      // Fallback to direct URL if can't extract assetId
      return url;
    }

    const mediaType = type || this.getMediaType(url);
    const cacheKey = this.getCacheKey(assetId, mediaType);

    // Check cache first
    const cachedUrl = await this.getCachedUrl(url, mediaType);
    if (cachedUrl) {
      return cachedUrl;
    }

    // Join an in-flight download rather than fetching the same asset twice.
    // The shared promise resolves to the Blob, never to a blob URL: each
    // caller mints its own URL below so that one caller revoking its URL can
    // never invalidate another caller's still-playing element.
    let downloadPromise = this.downloadingAssets.get(cacheKey);

    if (!downloadPromise) {
      downloadPromise = this.fetchAndCache(url, cacheKey, mediaType, signal);
      this.downloadingAssets.set(cacheKey, downloadPromise);

      // Housekeeping only - callers handle their own rejection below.
      downloadPromise
        .catch(() => {})
        .finally(() => {
          this.downloadingAssets.delete(cacheKey);
          this.abortControllers.delete(cacheKey);
        });
    }

    const blob = await downloadPromise;

    return this.createBlobUrl(blob, cacheKey);
  }

  // Mint an object URL for a blob and track it so it can be revoked later.
  private createBlobUrl(blob: Blob, cacheKey: string): string {
    const blobUrl = URL.createObjectURL(blob);
    this.blobUrlToAssetId.set(blobUrl, cacheKey);

    return blobUrl;
  }
  
  // Cancel a specific download (useful when component unmounts)
  cancelDownload(url: string, type?: MediaType): void {
    const assetId = this.extractAssetId(url);
    if (!assetId) return;

    const mediaType = type || this.getMediaType(url);
    const cacheKey = this.getCacheKey(assetId, mediaType);

    const abortController = this.abortControllers.get(cacheKey);
    if (abortController) {
      abortController.abort();
      this.abortControllers.delete(cacheKey);
      this.downloadingAssets.delete(cacheKey);
      console.log(`Cancelled download: ${cacheKey}`);
    }
  }

  // Fetch asset as blob (complete download, no Range requests)
  private async fetchAndCache(
    url: string,
    cacheKey: string,
    type: MediaType,
    signal?: AbortSignal,
  ): Promise<Blob> {
    try {
      // Create AbortController if not provided
      const abortController = signal
        ? undefined
        : new AbortController();
      const abortSignal = signal || abortController!.signal;

      if (abortController) {
        this.abortControllers.set(cacheKey, abortController);
      }

      const response = await fetch(url, {
        // No custom headers - avoid triggering CORS preflight requests
        // Browser will naturally fetch the complete file without Range requests
        signal: abortSignal, // Support cancellation
      });

      if (!response.ok) {
        throw new Error(`Failed to download ${type}: ${response.statusText}`);
      }

      // Download complete file as blob
      const blob = await response.blob();
      const contentType = blob.type || response.headers.get('content-type') || '';

      console.log(
        `Media downloaded: ${cacheKey} (${(blob.size / 1024 / 1024).toFixed(2)} MB)`,
      );

      // Cache the blob
      await this.cacheBlob(cacheKey, blob, type);

      return blob;
    } catch (error) {
      // Don't log error if it was aborted (expected behavior)
      if (error instanceof Error && error.name === 'AbortError') {
        console.log(`Download aborted: ${cacheKey}`);
      } else {
        console.error(`Error downloading ${type}:`, error);
      }
      throw error;
    }
  }

  // Cache blob with LRU management
  private async cacheBlob(
    cacheKey: string,
    blob: Blob,
    type: MediaType,
  ): Promise<void> {
    try {
      // Check if we need to evict old entries
      await this.ensureCacheSpace(blob.size);

      const cache = await caches.open(this.cacheName);
      const response = new Response(blob, {
        headers: {
          'Content-Type': blob.type || this.getContentType(type),
          'Content-Length': blob.size.toString(),
          'X-Cache-Version': this.currentCacheVersion, // Store version in header
        },
      });

      // Use Request object instead of string key
      const request = this.createCacheRequest(cacheKey);
      await cache.put(request, response);

      // Store metadata in LRU cache (automatically manages access order and TTL)
      this.metadataCache.set(cacheKey, {
        size: blob.size,
        type: type,
      });

      // Calculate and log cache statistics
      const totalSize = this.calculateCurrentCacheSize();
      const entryCount = this.metadataCache.size;
      
      console.log(
        `Media cached: ${cacheKey} (version: ${this.currentCacheVersion}) | ` +
        `Total entries: ${entryCount} | ` +
        `Total size: ${(totalSize / 1024 / 1024).toFixed(2)} MB / ${(this.maxCacheSize / 1024 / 1024).toFixed(2)} MB`
      );
    } catch (error) {
      console.error('Error caching media:', error);
      // Don't throw - caching failure shouldn't break playback
    }
  }

  // Calculate current total cache size from metadata
  private calculateCurrentCacheSize(): number {
    let size = 0;
    for (const metadata of this.metadataCache.values()) {
      size += metadata.size;
    }
    return size;
  }

  // Ensure cache has enough space (LRU eviction)
  // Note: LRU cache will automatically evict when maxSize is exceeded
  // The dispose callback will handle Cache API cleanup
  private async ensureCacheSpace(requiredSize: number): Promise<void> {
    let currentSize = this.calculateCurrentCacheSize();

    // If adding this would exceed the limit, manually evict oldest entries
    // until we have enough space (LRU cache will handle the rest automatically)
    while (currentSize + requiredSize > this.maxCacheSize) {
      // Get the oldest entry using LRU cache's rforEach (reverse order = oldest first)
      let oldestKey: string | null = null;
      this.metadataCache.rforEach((value, key) => {
        if (!oldestKey) {
          oldestKey = key;
        }
      });

      if (!oldestKey) {
        // No more entries to evict
        console.warn(
          `Cache full but cannot evict more entries. Required: ${requiredSize}, Current: ${currentSize}, Max: ${this.maxCacheSize}`,
        );
        break;
      }

      // Evict from LRU cache (dispose callback will handle Cache API cleanup)
      this.metadataCache.delete(oldestKey);
      
      // Recalculate current size
      currentSize = this.calculateCurrentCacheSize();
    }
  }

  // Note: Access order is now automatically managed by LRU cache
  // No need for manual updateAccessOrder method
  // Individual entry eviction is handled by handleEviction() via LRU dispose callback

  // Get content type for media type
  private getContentType(type: MediaType): string {
    switch (type) {
      case 'video':
        return 'video/mp4';
      case 'audio':
        return 'audio/mpeg';
      case 'image':
        return 'image/jpeg';
      default:
        return 'application/octet-stream';
    }
  }

  // Revoke blob URL (call when done using it)
  revokeBlobUrl(blobUrl: string): void {
    if (blobUrl.startsWith('blob:')) {
      URL.revokeObjectURL(blobUrl);
      this.blobUrlToAssetId.delete(blobUrl);
    }
  }

  // Clear all cache
  async clearCache(): Promise<void> {
    // Check if Cache API is available
    if (!('caches' in window)) {
      console.warn('Cache API not available, nothing to clear');
      return;
    }
    
    await this.clearCacheWithoutVersionCheck();
    
    // Re-store current version after clearing
    await this.checkCacheVersion();
  }

  // Get cache statistics
  async getCacheStats(): Promise<{
    size: number;
    maxSize: number;
    entryCount: number;
    usagePercent: number;
  }> {
    try {
      const totalSize = this.calculateCurrentCacheSize();

      return {
        size: totalSize,
        maxSize: this.maxCacheSize,
        entryCount: this.metadataCache.size,
        usagePercent: (totalSize / this.maxCacheSize) * 100,
      };
    } catch (error) {
      console.error('Error calculating cache stats:', error);
      return {
        size: 0,
        maxSize: this.maxCacheSize,
        entryCount: 0,
        usagePercent: 0,
      };
    }
  }

  // Set max cache size
  setMaxCacheSize(sizeInBytes: number): void {
    this.maxCacheSize = sizeInBytes;
  }
}

export const mediaCache = new MediaCache();

